import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../shared/ui/Button';
import type { EveningReminderFormErrors, EveningReminderFormValues } from '../types';
import { validateTime, validateTimezone } from '../lib/validation';
import styles from './EveningReminderForm.module.css';

interface EveningReminderFormProps {
  initialValues: EveningReminderFormValues;
  onSubmit: (values: EveningReminderFormValues) => void;
  isSaving: boolean;
  serverErrors?: EveningReminderFormErrors;
}

// PARA-136 — управляемая форма настроек вечернего напоминания.
export function EveningReminderForm({
  initialValues,
  onSubmit,
  isSaving,
  serverErrors,
}: EveningReminderFormProps): JSX.Element {
  const { t } = useTranslation('settings');
  const [values, setValues] = useState<EveningReminderFormValues>(initialValues);
  const [touched, setTouched] = useState<Record<keyof EveningReminderFormValues, boolean>>({
    evening_reminder_enabled: false,
    evening_reminder_time: false,
    evening_reminder_timezone: false,
  });
  const [errors, setErrors] = useState<EveningReminderFormErrors>({});

  // Синхронизируем форму с внешними начальными значениями (загрузка из API).
  useEffect(() => {
    setValues(initialValues);
    setTouched({
      evening_reminder_enabled: false,
      evening_reminder_time: false,
      evening_reminder_timezone: false,
    });
    setErrors({});
  }, [initialValues]);

  const validate = useCallback((next: EveningReminderFormValues): EveningReminderFormErrors => {
    const nextErrors: EveningReminderFormErrors = {};
    const timeError = validateTime(next.evening_reminder_time);
    if (timeError) {
      nextErrors.evening_reminder_time = timeError;
    }
    const timezoneError = validateTimezone(next.evening_reminder_timezone);
    if (timezoneError) {
      nextErrors.evening_reminder_timezone = timezoneError;
    }
    return nextErrors;
  }, []);

  const handleToggle = useCallback(() => {
    setValues((prev) => {
      const next = { ...prev, evening_reminder_enabled: !prev.evening_reminder_enabled };
      setErrors(validate(next));
      return next;
    });
  }, [validate]);

  const handleTimeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...values, evening_reminder_time: event.target.value };
      setValues(next);
      setTouched((prev) => ({ ...prev, evening_reminder_time: true }));
      setErrors(validate(next));
    },
    [values, validate],
  );

  const handleTimezoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...values, evening_reminder_timezone: event.target.value };
      setValues(next);
      setTouched((prev) => ({ ...prev, evening_reminder_timezone: true }));
      setErrors(validate(next));
    },
    [values, validate],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const validationErrors = validate(values);
      setErrors(validationErrors);
      setTouched({
        evening_reminder_enabled: true,
        evening_reminder_time: true,
        evening_reminder_timezone: true,
      });
      if (Object.keys(validationErrors).length === 0) {
        onSubmit(values);
      }
    },
    [onSubmit, validate, values],
  );

  const disabled = !values.evening_reminder_enabled;
  const mergedErrors: EveningReminderFormErrors = {
    ...errors,
    ...serverErrors,
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <label className={`${styles.row} ${styles.rowInline}`}>
        <span className={styles.label}>{t('form.enabledLabel')}</span>
        <input
          type="checkbox"
          checked={values.evening_reminder_enabled}
          onChange={handleToggle}
          className={styles.toggle}
          data-testid="evening-reminder-enabled"
        />
      </label>

      <label className={styles.row}>
        <span className={styles.label}>{t('form.timeLabel')}</span>
        <input
          type="time"
          value={values.evening_reminder_time}
          onChange={handleTimeChange}
          disabled={disabled || isSaving}
          className={styles.input}
          data-testid="evening-reminder-time"
        />
        {(touched.evening_reminder_time || serverErrors?.evening_reminder_time !== undefined) &&
          mergedErrors.evening_reminder_time && (
            <span className={styles.error} data-testid="time-error">
              {t(mergedErrors.evening_reminder_time)}
            </span>
          )}
      </label>

      <label className={styles.row}>
        <span className={styles.label}>{t('form.timezoneLabel')}</span>
        <input
          type="text"
          value={values.evening_reminder_timezone}
          onChange={handleTimezoneChange}
          disabled={disabled || isSaving}
          className={styles.input}
          data-testid="evening-reminder-timezone"
        />
        {(touched.evening_reminder_timezone ||
          serverErrors?.evening_reminder_timezone !== undefined) &&
          mergedErrors.evening_reminder_timezone && (
            <span className={styles.error} data-testid="timezone-error">
              {t(mergedErrors.evening_reminder_timezone)}
            </span>
          )}
      </label>

      {mergedErrors.general && (
        <p className={styles.error} data-testid="general-error">
          {t(mergedErrors.general)}
        </p>
      )}

      <Button type="submit" fullWidth disabled={isSaving} data-testid="save-settings">
        {isSaving ? t('form.saving') : t('form.save')}
      </Button>
    </form>
  );
}
